import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  hasAccess,
  NAVIGATION_ITEMS,
  type NavigationItem,
} from "../config/routes";

function flattenNav(items: NavigationItem[]): NavigationItem[] {
  const out: NavigationItem[] = [];
  for (const item of items) {
    out.push(item);
    if (item.children?.length) {
      out.push(...flattenNav(item.children));
    }
  }
  return out;
}

/** Longest matching nav path for the current location (tool/role gate). */
function findNavItemForPath(pathname: string): NavigationItem | undefined {
  const flat = flattenNav(NAVIGATION_ITEMS);
  let best: NavigationItem | undefined;
  for (const item of flat) {
    if (!item.path) continue;
    if (pathname === item.path || pathname.startsWith(item.path + "/")) {
      if (!best || item.path.length > (best.path?.length ?? 0)) {
        best = item;
      }
    }
  }
  return best;
}

export const AdminProtectedRoute = () => {
  const location = useLocation();
  const currentUser = useSelector((state: any) => state.googleAuth.user);
  const isAdmin = currentUser?.roles?.role === "admin" || false;

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} />;
  }
  return isAdmin ? <Outlet /> : <Navigate to="/homepage" />;
};

export const PlatformsProtectedRoute = () => {
  const location = useLocation();
  const currentUser = useSelector((state: any) => state.googleAuth.user);
  const isAdmin = currentUser?.roles?.role === "admin" || false;
  const isUser = currentUser?.roles?.role === "user" || false;
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} />;
  }
  return isUser || isAdmin ? <Outlet /> : <Navigate to="/homepage" />;
};

/**
 * Logged-in gate + same tool/role checks as the sidebar (`hasAccess`).
 * Whitelist email allowlist inside YouTubeScorecard stays separate (product decision).
 */
export const ProtectedRoute = () => {
  const location = useLocation();
  const loggedIn = useSelector((state: any) => state.googleAuth.isLoggedIn);
  const currentUser = useSelector((state: any) => state.googleAuth.user);

  if (!loggedIn) {
    return <Navigate to="/login" state={{ from: location }} />;
  }

  const navItem = findNavItemForPath(location.pathname);
  if (navItem && (navItem.requiredTool || navItem.requiredRole)) {
    const roles = currentUser?.roles || { role: "", tools: [] };
    if (!hasAccess(navItem, roles)) {
      return <Navigate to="/homepage" replace />;
    }
  }

  return <Outlet />;
};
